import React, { useState, useEffect } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import StudentAnnouncementCard from "../../../components/customCoursework/StudentAnnouncementCard/StudentAnnouncementCard";
import {
  useGetAnnouncementsForStudentQuery,
  useMarkAnnouncementAsSeenMutation,
} from "../../../graphql/generated/graphql";

interface StudentAnnouncementContainerProps {
  courseId: string;
}

const StudentAnnouncementContainer: React.FC<StudentAnnouncementContainerProps> = ({ courseId }) => {
  const [showAll, setShowAll] = useState(false);

  const {
    data: announcementsData,
    loading,
    error,
    refetch,
  } = useGetAnnouncementsForStudentQuery({ variables: { courseId } });

  const [markAsSeenMutation] = useMarkAnnouncementAsSeenMutation();
  const announcements = announcementsData?.getAnnouncementsForStudent?.data || [];

  useEffect(() => {
    const visibleAnnouncements = showAll ? announcements : announcements.slice(0, 2);

    visibleAnnouncements.forEach(async (announcement) => {
      if (announcement.isNew) {
        try {
          await markAsSeenMutation({ variables: { announcementId: announcement.id } });
        } catch (error) {
          console.error("Failed to mark announcement as seen:", error);
        }
      }
    });
  }, [announcements, showAll, markAsSeenMutation]);

  const handleViewAllToggle = () => {
    setShowAll(!showAll);
  };

  if (loading || error || announcements.length === 0) return null;

  const visibleAnnouncements = showAll ? announcements : announcements.slice(0, 2);
  const hasMoreAnnouncements = announcements.length > 2;

  return (
    <View style={styles.container}>
      <View style={styles.announcementsList}>
        <View style={styles.cardsContainer}>
          <View style={styles.header}>
            <CustomText text="Announcements" size="s" weight="bold" style={styles.title} useTranslationText={false} />
            {hasMoreAnnouncements && (
              <TouchableOpacity onPress={handleViewAllToggle} style={styles.viewAllButton}>
                <CustomText
                  text={showAll ? "Show Less" : "View All"}
                  size="s"
                  style={styles.viewAllText}
                  useTranslationText={false}
                />
              </TouchableOpacity>
            )}
          </View>

          <CustomSpacing type="vertical" size="m" />

          {announcements.length === 1 ? (
            // Single announcement - full width
            <StudentAnnouncementCard announcement={visibleAnnouncements[0]} isInGrid={false} />
          ) : (
            // Multiple announcements - 2 column grid
            <View style={styles.gridContainer}>
              {visibleAnnouncements.map((announcement, index) => (
                <View key={announcement.id} style={styles.gridItem}>
                  <StudentAnnouncementCard announcement={announcement} isInGrid={true} />
                </View>
              ))}
            </View>
          )}

          {!showAll && hasMoreAnnouncements && (
            <>
              <CustomSpacing type="vertical" size="m" />
              <View style={styles.moreAnnouncementsIndicator}>
                <CustomText
                  text={`+ ${announcements.length - 2} More Announcement${announcements.length - 2 === 1 ? "" : "s"}`}
                  size="s"
                  style={styles.moreAnnouncementsText}
                  useTranslationText={false}
                />
              </View>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    paddingTop: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    color: "#000",
  },
  viewAllButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  viewAllText: {
    color: "#3B82F6",
    fontWeight: "500",
  },
  announcementsList: {
    width: "100%",
  },
  cardsContainer: {
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 20,
    padding: 20,
  },
  gridContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 16,
  },
  gridItem: {
    width: "49%",
    minWidth: 300,
  },
  moreAnnouncementsIndicator: {
    alignItems: "center",
    paddingVertical: 12,
  },
  moreAnnouncementsText: {
    color: "#475467",
    fontWeight: "500",
  },
});

export default StudentAnnouncementContainer;
